/*
 * This function processes the input value of an input number field.
 * It removes any characters that are not digits, e/E, +, -, or .,
 * ensures only one "e" or "E" is allowed, and it can't be at the start,
 * ensures only one "+" or "-" at the start or immediately after "e" or "E,
 * allows only one decimal point in the base part,
 * enforces precision on the base part (limit decimal places),
 * enforces min/max limits on the complete number.
 * @param {string} value - The input value to be processed.
 * @param {number} minValue - The minimum value allowed.
 * @param {number} maxValue - The maximum value allowed.
 * @param {number} precision - The number of decimal places allowed.
 * @param {number} defaultValue - The default value to return if the input value is empty.
 * @returns {number | null} The processed numeric value, or null if the value is out of range.
 * @example
 * Returns '123.45'
 * processNumericValue('123.45', 0, 999, 2, 0);
 */
function processNumericValue(value, minValue, maxValue, precision) {
  let numericValue = String(value);

  if (minValue >= 0 && numericValue.startsWith('-')) {
    return null;
  }

  // Step 1: Remove any characters that are not digits, +, -, or .
  numericValue = numericValue.replace(/[^0-9+\-.]/g, '');

  // Step 3: Ensure only one "+" or "-" at the start or immediately after ""e or "E"
  // 3.1. Remove consecutive "+" or "-" signs that are not allowed in valid positions.
  numericValue = numericValue.replace(/(?!^|[eE])[+-]+/g, ''); // Remove "+" or "-" not at the start or after "e" or "E"

  // 3.2. Allow only one "+" or "-" at the start, if it's the very first character.
  numericValue = numericValue.replace(/^([+-]){2,}/, '$1'); // If multiple signs at the start, keep only the first

  // 3.3. Restrict multiple "+" or "-" signs following "e" or "E".
  // numericValue = numericValue.replace(/([eE])[+-]{2,}/g, '$1'); // Keep only one "+" or "-" after "e" or "E"

  // Step 4: Allow only one decimal point in the base part (before "e" or "E")
  const [base, exponent] = numericValue.split(/e|E/);
  const baseParts = base.split('.');

  if (baseParts.length > 2) {
    numericValue = `${baseParts[0]}.${baseParts[1]}`; // Keep only the first decimal in the base part
  } else {
    numericValue = base + (exponent !== undefined ? `e${exponent}` : '');
  }

  // Step 5: Enforce precision on the base part (limit decimal places before "e/E")
  if (baseParts[1] === '' && !precision) {
    const [val] = baseParts;
    numericValue = val;
  }

  if (baseParts[1] && baseParts[1].length > precision) {
    numericValue = `${baseParts[0]}.${baseParts[1].slice(0, precision)}${exponent ? `e${exponent}` : ''}`;
  }

  // Step 6: Enforce min/max limits on the base part (with some improvement)
  const numberValue = parseFloat(numericValue);

  if (!Number.isNaN(numberValue) && (value !== 0 || value !== 0.0)) {
    if (numberValue > maxValue) {
      return maxValue;
    }
    if (numberValue < minValue && (!numericValue.includes('.') || !numericValue.endsWith('.'))) {
      return minValue;
    }
  }

  // Step 7: Enforce min/max limits on the complete number
  // const numberValue = parseFloat(numericValue);
  // const isMinValueFloat = String(minValue).includes('.');

  // if (
  //   numberValue > maxValue ||
  //   (numberValue < minValue &&
  //     (!isMinValueFloat ||
  //       (isMinValueFloat && (numericValue.includes('.') || numericValue.includes('-')) && !numericValue.endsWith('.'))))
  // ) {
  //   return null;
  // }

  // Step 8: Change the value to 0 if the input value is integer but starts with zero, e.g. 01, 02, etc.
  if (numericValue[0] === '0' && numericValue[1] !== undefined && numericValue[1] !== '.') {
    const [, ...restValue] = numericValue;

    return restValue.join('');
  }

  return numericValue;
}

export default processNumericValue;
